import { DateTime } from "luxon";
import { FilterTypes } from "../../../../types";
import { POLK_ATTRIBUTION_MODEL_FIELDS } from "./configuration";
export { POLK_ATTRIBUTION_MODEL_FIELDS };
/**
 * Default lookback window (in days) applied to the Polk attribution explore
 * when the caller doesn't specify one. Mirrors the LookML parameter default
 * (polk_attribution.pmt_lookback_window, default_value: "30").
 */
export const POLK_ATTRIBUTION_DEFAULT_LOOKBACK_DAYS = 30;
// Model fields backed by a yesno-type LookML dimension — see the "Yes"/"No"
// comment on PolkAttributionQueryFields above.
const POLK_ATTRIBUTION_YESNO_MODEL_FIELDS = new Set([
    "hasAdvertiserAddress",
    "zipAdvertiserMatch",
    "postalMatch"
]);
export const POLK_ATTRIBUTION_MODEL_TO_QUERY_MAP = {
    impressionDate: "polk_attribution.impression_timestamp_date",
    impressionWeek: "polk_attribution.impression_timestamp_week",
    impressionMonth: "polk_attribution.impression_timestamp_month",
    advertiserId: "polk_attribution.advertiser_id",
    advertiserName: "meta_inst.advertiser_name",
    campaignId: "meta_inst.campaign_id",
    campaignName: "meta_inst.campaign_name",
    lineitemName: "meta_inst.lineitem_name",
    instId: "polk_attribution.inst_id",
    creativeId: "polk_attribution.creative_id",
    creativeName: "meta_creative.creative_name",
    publisherName: "meta_publisher.publisher_name",
    deviceType: "polk_attribution.device_type",
    country: "polk_attribution.country",
    postalCode: "polk_attribution.postal",
    channel: "polk_attribution.channel",
    network: "polk_attribution.network",
    mediaType: "polk_attribution.media_type",
    mediaSubtype: "polk_attribution.media_subtype",
    impressionCost: "polk_attribution.impression_cost",
    baseMediaCharges: "polk_attribution.base_media_charges_usd",
    purchaseDate: "polk_attribution.purchase_date",
    purchaseWeek: "polk_attribution.purchaseweek",
    daysToPurchase: "polk_attribution.days_to_purchase",
    vehicleMake: "polk_attribution.make",
    vehicleModel: "polk_attribution.model",
    vehicleTrim: "polk_attribution.trim",
    vehicleModelYear: "polk_attribution.modelyear",
    vehicleCondition: "polk_attribution.newusedind",
    dealershipName: "polk_attribution.sellingdlrnm",
    dealershipCity: "polk_attribution.sellingdlrcity",
    dealershipState: "polk_attribution.sellingdlrstate",
    dealershipZip: "polk_attribution.sellingdlrzip",
    hasAdvertiserAddress: "polk_attribution.has_advertiser_address",
    zipAdvertiserMatch: "polk_attribution.zip_advertiser_match",
    postalMatch: "polk_attribution.postal_match",
    metroName: "delivery_geo.metro_name",
    metroCode: "delivery_geo.metro_code",
    impressions: "polk_attribution.impressions",
    reach: "polk_attribution.reach",
    toplineImpressions: "polk_attribution.topline_impressions",
    toplineReach: "polk_attribution.topline_reach",
    sales: "polk_attribution.sales",
    totalMsrp: "polk_attribution.estimated_revenue",
    averagePrice: "polk_attribution.avg_price",
    buyThroughRate: "polk_attribution.topline_btr"
};
/**
 * Transforms a list of model fields into a list of explore field names.
 * @param fields The list of model fields that are going to be requested from the explore
 * @returns A list of raw field names from the explore
 */
export const polkAttributionModelFieldsToQueryDimensions = (fields) => {
    const queryDimensions = new Set();
    for (const field of fields) {
        queryDimensions.add(POLK_ATTRIBUTION_MODEL_TO_QUERY_MAP[field]);
    }
    return Array.from(queryDimensions);
};
/**
 * Builds the camelCase model object from the requested fields and the raw
 * Looker query response row.
 * @param fields The list of model fields that were requested from the explore
 * @param queryResponse The response row from the explore query
 * @returns An object containing the requested fields with their matching response value
 */
export const polkAttributionQueryDimensionsToModel = (fields, queryResponse) => {
    const model = {};
    for (const field of fields) {
        const rawValue = queryResponse[POLK_ATTRIBUTION_MODEL_TO_QUERY_MAP[field]];
        model[field] =
            POLK_ATTRIBUTION_YESNO_MODEL_FIELDS.has(field) && rawValue != null
                ? rawValue === "Yes"
                : rawValue;
    }
    return model;
};
const parseStringFilter = (filter) => {
    switch (filter.type) {
        case FilterTypes.EQ: {
            if (typeof filter.value !== "string") {
                throw new Error(`Filter ${String(filter.field)} must be a string`);
            }
            return filter.value;
        }
        case FilterTypes.IN: {
            if (!Array.isArray(filter.value)) {
                throw new Error(`Filter ${String(filter.field)} must be an array of strings`);
            }
            return filter.value.join(",");
        }
        default:
            throw new Error(`Filter ${filter.type} is not supported for the field ${String(filter.field)}`);
    }
};
const parseNumberFilter = (filter) => {
    switch (filter.type) {
        case FilterTypes.EQ: {
            if (typeof filter.value !== "number") {
                throw new Error(`Filter ${String(filter.field)} must be a number`);
            }
            return filter.value.toString();
        }
        default:
            throw new Error(`Filter ${filter.type} is not supported for the field ${String(filter.field)}`);
    }
};
/**
 * Formats a date-range filter the same way the Attribution/Inventory modules
 * do: "yyyy/MM/dd to yyyy/MM/dd", with the end date bumped by one day since
 * Looker treats the end date of a date filter as exclusive.
 */
export const parsePolkAttributionDateFilter = (filter) => {
    if (filter.type !== FilterTypes.BETWEEN || !Array.isArray(filter.value)) {
        throw new Error(`Filter ${String(filter.field)} must be a date range (BETWEEN) filter`);
    }
    const [startDate, endDate] = filter.value;
    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
        throw new Error(`Filter ${String(filter.field)} must contain two dates when filtering for a date range`);
    }
    const formattedStartDate = DateTime.fromJSDate(startDate).toFormat("yyyy/MM/dd");
    const formattedEndDate = DateTime.fromJSDate(endDate).plus({ day: 1 }).toFormat("yyyy/MM/dd");
    return `${formattedStartDate} to ${formattedEndDate}`;
};
export const polkAttributionModelFiltersToQueryFilters = (filters) => {
    const dateFilter = filters?.find((filter) => filter.field === "dates");
    if (!dateFilter) {
        throw new Error("You must provide a dates filter in order to query the Polk attribution explore");
    }
    const lookbackFilter = filters?.find((filter) => filter.field === "lookbackDays");
    const lookbackDays = lookbackFilter
        ? Number(parseNumberFilter(lookbackFilter))
        : POLK_ATTRIBUTION_DEFAULT_LOOKBACK_DAYS;
    const queryFilters = {
        "polk_attribution.flt_analysis_interval": parsePolkAttributionDateFilter(dateFilter),
        "polk_attribution.pmt_lookback_window": lookbackDays.toString()
    };
    const advertiserFilter = filters?.find((filter) => filter.field === "advertiserIds");
    if (advertiserFilter?.value && advertiserFilter.value.length > 0) {
        queryFilters["polk_attribution.advertiser_id"] = parseStringFilter(advertiserFilter);
    }
    const campaignFilter = filters?.find((filter) => filter.field === "campaignIds");
    if (campaignFilter?.value && campaignFilter.value.length > 0) {
        queryFilters["meta_inst.campaign_id"] = parseStringFilter(campaignFilter);
    }
    const vehicleConditionFilter = filters?.find((filter) => filter.field === "vehicleConditions");
    if (vehicleConditionFilter?.value && vehicleConditionFilter.value.length > 0) {
        queryFilters["polk_attribution.newusedind"] = parseStringFilter(vehicleConditionFilter);
    }
    const metroFilter = filters?.find((filter) => filter.field === "metroIds");
    if (metroFilter?.value && metroFilter.value.length > 0) {
        queryFilters["delivery_geo.metro_code"] = parseStringFilter(metroFilter);
    }
    const scopeToMatchedDealershipFilter = filters?.find((filter) => filter.field === "scopeToMatchedDealership");
    if (scopeToMatchedDealershipFilter?.value === true) {
        queryFilters["polk_attribution.is_dealership_matched"] = "yes";
    }
    return queryFilters;
};
//# sourceMappingURL=index.js.map